import { Button, Listy, Spin, Tooltip } from 'antd';
import clsx from 'clsx';
import { useCallback, useMemo, useState } from 'react';
import type File from '../../../interfaces/File';
import {
  getDefaultFiltersForListType,
  useFileShortcutForm,
} from '../../../hooks/useFileShortcutForm';
import { FILE_PREVIEW_EXTENSIONS_IMAGES, getFileTitle, getFolderHex } from '../../../utils/utils';
import FileIcon from '../../Common/FileIcon';
import { LISTY_ITEM_NO_HOVER } from '../../Common/listyItemClassNames';
import TruncatedTooltip from '../../Common/TruncatedTooltip';
import { Icon } from '../../Core/Icon';
import ModalLayout from '../../../layouts/ModalLayout';
import {
  canCreateFolderInPicker,
  filterPickerFilesBySearch,
  FolderPickerBreadcrumb,
  FolderPickerCreateContextMenu,
  FolderPickerToolbar,
  useFolderPickerCreateFolder,
  useFolderPickerGlobalSearch,
} from '../FolderPicker';

/** Fixed list area height in shortcut modal folder picker. */
const FILE_SHORTCUT_LIST_VIEWPORT_HEIGHT = 320;

const MOVE_LISTY_ITEM_CLASS = clsx(LISTY_ITEM_NO_HOVER, 'p-0!');
const MOVE_ROW_CLASS = 'flex flex-row items-center justify-between rounded px-2 py-2';
const MOVE_ROW_INNER_CLASS = 'flex min-w-0 flex-1 flex-row items-center gap-2';
const MOVE_ROW_TITLE_CLASS = 'w-full truncate';

interface RootPickerItem {
  id: 'personal' | 'received' | 'favorites';
  listType: 'personal' | 'received' | 'favorites';
  iconName: 'FolderIcon' | 'FileDownIcon' | 'StarIcon';
  labelKey: 'common.files' | 'common.received' | 'common.favorites';
}

const ROOT_PICKER_ITEMS: RootPickerItem[] = [
  { id: 'personal', listType: 'personal', iconName: 'FolderIcon', labelKey: 'common.files' },
  { id: 'received', listType: 'received', iconName: 'FileDownIcon', labelKey: 'common.received' },
  { id: 'favorites', listType: 'favorites', iconName: 'StarIcon', labelKey: 'common.favorites' },
];

interface Props {
  /** Optional when reused as a generic destination folder picker. */
  selectedFile?: File;
  onCancel: () => void;
  onSubmit: (folderUid: number | null, listType?: 'personal' | 'received' | 'favorites') => void;
  currentFolder?: File | null;
  /** Defaults to `common.create`. */
  submitLabel?: string;
  /**
   * Empty-state hint under the list. Defaults to shortcut copy.
   * Prefer `hint` (already translated) or `hintKey` (i18n key).
   */
  hint?: string;
  hintKey?: string;
}

export const FileShortcutForm = ({
  selectedFile,
  onSubmit,
  onCancel,
  currentFolder,
  submitLabel,
  hint,
  hintKey,
}: Props) => {
  const {
    t,
    isLoading,
    selectedListType,
    setSelectedListType,
    filters,
    setFilters,
    files,
    selectedFolder,
    setSelectedFolder,
    setIsNavigatingIntoFolder,
    isFilesRootSelected,
    setIsFilesRootSelected,
    isAtRootLevel,
    isSelectedFileMine,
    currentFolderParent,
    openFolderOrRequestPassword,
    selectFolderOrRequestPassword,
    isFileDisabled,
    currentPositionLabel,
    handleScroll,
    parent,
    authenticatedUser,
    handleSearchChange,
  } = useFileShortcutForm({ selectedFile, currentFolder });

  const canCreateFolder = useMemo(
    () =>
      canCreateFolderInPicker({
        isAtRootLevel,
        listType: selectedListType,
        parentFolder: parent ?? null,
        authenticatedUserId: authenticatedUser?.id,
      }),
    [authenticatedUser?.id, isAtRootLevel, parent, selectedListType],
  );

  const getCreateParentId = useCallback(() => filters.parent || undefined, [filters.parent]);

  const { openCreateFolder } = useFolderPickerCreateFolder({
    getParentId: getCreateParentId,
    onCreated: openFolderOrRequestPassword,
  });

  const [exactMatch, setExactMatch] = useState(false);

  const { globalSearchResults, isGlobalSearching, isGlobalSearchActive } =
    useFolderPickerGlobalSearch({
      enabled: isAtRootLevel,
      keyword: filters.search ?? '',
      exactMatch,
    });

  const listRootLabel = useMemo(() => {
    if (selectedListType === 'received') return t('common.received');
    if (selectedListType === 'favorites') return t('common.favorites');
    return t('common.files');
  }, [selectedListType, t]);

  const handleBreadcrumbListRootClick = useCallback(() => {
    if (isSelectedFileMine || selectedListType === 'personal') {
      setFilters(getDefaultFiltersForListType('personal'));
      setSelectedFolder(null);
      setIsNavigatingIntoFolder(false);
      setIsFilesRootSelected(false);
      if (!isSelectedFileMine) {
        setSelectedListType('personal');
      }
    } else if (selectedListType === 'received') {
      setFilters(getDefaultFiltersForListType('received'));
      setSelectedFolder(null);
      setIsNavigatingIntoFolder(false);
      setIsFilesRootSelected(false);
      setSelectedListType('received');
    } else if (selectedListType === 'favorites') {
      setFilters(getDefaultFiltersForListType('favorites'));
      setSelectedFolder(null);
      setIsNavigatingIntoFolder(false);
      setIsFilesRootSelected(false);
      setSelectedListType('favorites');
    }
  }, [
    isSelectedFileMine,
    selectedListType,
    setFilters,
    setIsFilesRootSelected,
    setIsNavigatingIntoFolder,
    setSelectedFolder,
    setSelectedListType,
  ]);

  const handleBreadcrumbPathNavigate = useCallback(
    (fileId: string) => {
      setFilters((prev) => ({ ...prev, offset: 0, parent: fileId }));
    },
    [setFilters],
  );

  const emptyHint =
    hint ?? (hintKey ? t(hintKey) : t('modal.fileShortcutForm.selectFolderToCreateShortcutInto'));

  const navigateIntoListRoot = useCallback(
    (listType: 'personal' | 'received' | 'favorites') => {
      setIsFilesRootSelected(false);
      setSelectedListType(listType);
      setIsNavigatingIntoFolder(true);
      setFilters(getDefaultFiltersForListType(listType));
    },
    [setFilters, setIsFilesRootSelected, setIsNavigatingIntoFolder, setSelectedListType],
  );

  const renderRootRow = useCallback(
    (item: RootPickerItem) => {
      const label = t(item.labelKey);
      const isSelected =
        item.listType === 'personal'
          ? !selectedFolder && selectedListType === 'personal' && isFilesRootSelected
          : selectedListType === item.listType && !selectedFolder;

      return (
        <div
          className={clsx(
            MOVE_ROW_CLASS,
            'text-blue-dark hover:bg-gray cursor-pointer',
            isSelected && 'bg-light',
          )}
          onClick={() => {
            setSelectedFolder(null);
            setSelectedListType(item.listType);
            setIsNavigatingIntoFolder(false);
            setIsFilesRootSelected(item.listType === 'personal');
          }}
          onDoubleClick={() => {
            if (item.listType === 'personal') {
              setSelectedFolder(null);
            }
            navigateIntoListRoot(item.listType);
          }}
        >
          <div className={MOVE_ROW_INNER_CLASS}>
            <Icon name={item.iconName} className="h-6 w-6 shrink-0" />
            <span className={clsx(MOVE_ROW_TITLE_CLASS, 'text-azure')}>{label}</span>
          </div>
          <div className="ml-auto shrink-0">
            <Button
              type="text"
              aria-label={t('a11y.openList', { name: label })}
              onClick={(e) => {
                e.stopPropagation();
                navigateIntoListRoot(item.listType);
              }}
            >
              <Icon name="ChevronRightIcon" className="w-5" />
            </Button>
          </div>
        </div>
      );
    },
    [
      isFilesRootSelected,
      navigateIntoListRoot,
      selectedFolder,
      selectedListType,
      setIsFilesRootSelected,
      setIsNavigatingIntoFolder,
      setSelectedFolder,
      setSelectedListType,
      t,
    ],
  );

  const renderFolderRow = useCallback(
    (f: File) => {
      // Shortcut destinations are folders only — files always disabled (incl. global search hits).
      const disabled = isFileDisabled(f);

      return (
        <div
          className={clsx(
            MOVE_ROW_CLASS,
            disabled ? 'pointer-events-none opacity-50' : 'hover:bg-gray cursor-pointer',
            selectedFolder?.id === f.id && 'bg-light',
          )}
          aria-disabled={disabled || undefined}
          onDoubleClick={() => {
            if (!disabled && f.isFolder) {
              openFolderOrRequestPassword(f);
            }
          }}
          onClick={() => {
            if (!disabled && f.isFolder) {
              selectFolderOrRequestPassword(f);
            }
          }}
        >
          <div className={MOVE_ROW_INNER_CLASS}>
            <FileIcon
              fileId={f.id}
              allowedFilePreviewExtensions={FILE_PREVIEW_EXTENSIONS_IMAGES}
              isFolder={f.isFolder}
              isFolderPasswordProtected={f.isPasswordSet && !f.isPasswordBypass}
              fileExtension={f.fileExtension}
              className="h-6 w-6 shrink-0"
              style={{
                color: getFolderHex(f),
              }}
            />
            <span className={MOVE_ROW_TITLE_CLASS}>
              <TruncatedTooltip>{getFileTitle(f)}</TruncatedTooltip>
            </span>
          </div>
          <div className="ml-auto shrink-0">
            {f.isFolder && (
              <Button
                type="text"
                aria-label={t('a11y.openFolder', { name: getFileTitle(f) })}
                onClick={(e) => {
                  e.stopPropagation();
                  openFolderOrRequestPassword(f);
                }}
              >
                <Icon name="ChevronRightIcon" className="w-5" />
              </Button>
            )}
          </div>
        </div>
      );
    },
    [
      isFileDisabled,
      openFolderOrRequestPassword,
      selectFolderOrRequestPassword,
      selectedFolder?.id,
      t,
    ],
  );

  const visibleFiles = useMemo(
    () => (isAtRootLevel ? globalSearchResults : filterPickerFilesBySearch(files, filters.search)),
    [files, filters.search, globalSearchResults, isAtRootLevel],
  );

  const isSearchBusy = (isGlobalSearchActive && isGlobalSearching) || (!isAtRootLevel && isLoading);
  const isInitialLoading = isSearchBusy && visibleFiles.length === 0;
  const isLoadingMore = isLoading && files.length > 0 && !isAtRootLevel;
  const showRootPicker = isAtRootLevel && !isGlobalSearchActive;
  const showSearchOrFolderList = !showRootPicker;
  const showEmpty = visibleFiles.length === 0 && !isSearchBusy && showSearchOrFolderList;

  return (
    <ModalLayout>
      <ModalLayout.Content>
        <div className="text-gray-dark text-sm">
          <span>{t('modal.fileShortcutForm.currentPosition')}:</span>
          <span className="ml-1">{currentPositionLabel}</span>
        </div>

        <div
          className={clsx(
            'flex flex-row items-center pt-2',
            isAtRootLevel && 'pointer-events-none opacity-0',
          )}
        >
          <Tooltip title={t('modal.fileShortcutForm.goBack')}>
            <Button
              type="text"
              className="p-0!"
              aria-label={t('a11y.goBack')}
              onClick={() => {
                if (isAtRootLevel) {
                  return;
                }
                setSelectedFolder(null);
                setIsFilesRootSelected(false);
                if (currentFolderParent) {
                  setFilters({
                    ...filters,
                    offset: 0,
                    parent: currentFolderParent?.fileId || undefined,
                  });
                } else {
                  setFilters(getDefaultFiltersForListType('personal'));
                  setIsNavigatingIntoFolder(false);
                  if (!isSelectedFileMine) {
                    setSelectedListType('personal');
                  }
                }
              }}
            >
              <Icon name="ChevronLeftIcon" className="h-6 w-6" />
            </Button>
          </Tooltip>
          <span className="ml-1">
            {isAtRootLevel
              ? selectedListType === 'received'
                ? t('common.received')
                : selectedListType === 'favorites'
                  ? t('common.favorites')
                  : t('common.files')
              : currentFolderParent?.title}
          </span>
        </div>
        <div className="border-gray-disabled border-b" />

        <FolderPickerToolbar
          search={filters.search ?? ''}
          onSearchChange={handleSearchChange}
          searchMode={isAtRootLevel ? 'global' : 'inFolder'}
          exactMatch={exactMatch}
          onExactMatchChange={setExactMatch}
          canCreateFolder={canCreateFolder}
          onCreateFolder={openCreateFolder}
        />

        <div
          className="relative min-h-[320px]"
          style={{ height: FILE_SHORTCUT_LIST_VIEWPORT_HEIGHT }}
        >
          {showEmpty ? (
            <FolderPickerCreateContextMenu
              enabled={canCreateFolder}
              onCreateFolder={openCreateFolder}
            >
              <div className="flex h-full items-center justify-center overflow-hidden">
                <div className="flex flex-col items-center justify-center gap-2">
                  <Icon name="FolderIcon" className="h-36 w-36 opacity-25" />
                  <span className="text-gray-dark text-sm">
                    {isGlobalSearchActive
                      ? t('common.noFilesFound')
                      : t('modal.fileShortcutForm.folderIsEmpty')}
                  </span>
                </div>
              </div>
            </FolderPickerCreateContextMenu>
          ) : isInitialLoading ? (
            <div className="flex h-full items-center justify-center" aria-busy="true">
              <Spin />
            </div>
          ) : showRootPicker ? (
            <Listy
              items={ROOT_PICKER_ITEMS}
              rowKey="id"
              height={FILE_SHORTCUT_LIST_VIEWPORT_HEIGHT}
              classNames={{ item: MOVE_LISTY_ITEM_CLASS }}
              itemRender={renderRootRow}
            />
          ) : (
            <FolderPickerCreateContextMenu
              enabled={canCreateFolder}
              onCreateFolder={openCreateFolder}
            >
              <>
                <Listy
                  items={visibleFiles}
                  rowKey="id"
                  virtual
                  height={FILE_SHORTCUT_LIST_VIEWPORT_HEIGHT}
                  onScroll={handleScroll}
                  classNames={{ item: MOVE_LISTY_ITEM_CLASS }}
                  itemRender={renderFolderRow}
                />
                {isLoadingMore && (
                  <div className="pointer-events-none absolute right-0 bottom-0 left-0 flex justify-center pb-2">
                    <Spin size="small" />
                  </div>
                )}
              </>
            </FolderPickerCreateContextMenu>
          )}
        </div>

        <div className="border-gray-disabled border-t" />
        <div className="min-w-0 overflow-hidden pt-2 text-sm">
          {isFilesRootSelected && !selectedFolder ? (
            <span>{t('common.files')}</span>
          ) : !selectedFolder && !isAtRootLevel ? (
            <span>{emptyHint}</span>
          ) : !selectedFolder && isAtRootLevel && !isFilesRootSelected ? (
            <span>{emptyHint}</span>
          ) : (
            <FolderPickerBreadcrumb
              listRootLabel={listRootLabel}
              onListRootClick={handleBreadcrumbListRootClick}
              path={selectedFolder?.path}
              currentTitle={selectedFolder?.fileName}
              currentId={selectedFolder?.id}
              onPathNavigate={handleBreadcrumbPathNavigate}
            />
          )}
        </div>
      </ModalLayout.Content>

      <ModalLayout.Footer>
        <div className="flex flex-row items-center justify-end gap-2">
          <Button size="middle" onClick={() => onCancel()}>
            {t('common.cancel')}
          </Button>
          <Button
            size="middle"
            type="primary"
            disabled={
              !selectedFolder &&
              !(isAtRootLevel && selectedListType === 'personal' && isFilesRootSelected)
            }
            onClick={() => {
              if (isAtRootLevel && selectedListType === 'personal') {
                onSubmit(null, 'personal');
              } else {
                onSubmit(selectedFolder?.id || null, selectedListType);
              }
            }}
          >
            {submitLabel ?? t('common.create')}
          </Button>
        </div>
      </ModalLayout.Footer>
    </ModalLayout>
  );
};

export default FileShortcutForm;
